import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import AddressAutocomplete from '../components/AddressAutocomplete';
import '../styles/Booking.css';

interface MerchItem {
  id: string;
  name: string;
  price: number;
  description: string;
  image: string;
}

const Checkout: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedEdition = queryParams.get('edition') || 'Standard';
  
  const { user, token } = useAuth();
  const [item, setItem] = useState<MerchItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordered, setOrdered] = useState(false);
  
  const RUSH_FEE = 5;
  const LATE_FEE = 2.50;

  const getNextSunday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() + (7 - day) % 7 + (day === 0 ? 7 : 0);
    const nextSunday = new Date(d.setDate(diff));
    return nextSunday.toISOString().split('T')[0];
  };

  const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const [standardDate] = useState(getNextSunday());
  const [tomorrowDate] = useState(getTomorrow());
  const [isAfterCutoff] = useState(() => {
    const day = new Date().getDay();
    return day === 0 || day >= 4; // Sunday (0), Thursday (4), Friday (5), Saturday (6)
  });

  // Form State
  const [address, setAddress] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryOption, setDeliveryOption] = useState<'standard' | 'rush'>('standard');
  const [customerName, setCustomerName] = useState(user?.name || '');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetch(`${API_URL}/api/merchandise/${id}`)
      .then(res => res.json())
      .then(data => {
        setItem(data || null);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching item:', err);
        setLoading(false);
      });
  }, [id, user, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const nsRegex = /(Nova Scotia|NS|B[0-9][A-Z] [0-9][A-Z][0-9])/i;
    if (!nsRegex.test(address)) {
      setError('Invalid address: Nova Scotia delivery only. Please include "NS".');
      return;
    }

    if (!address || !customerName || !signature.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    const finalDeliveryDate = deliveryOption === 'rush' ? tomorrowDate : standardDate;
    const currentShippingFee = deliveryOption === 'rush' ? RUSH_FEE : (isAfterCutoff ? LATE_FEE : 0);
    const totalPrice = (item?.price || 0) * quantity + currentShippingFee;

    fetch(`${API_URL}/api/notifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        parentEmail: user?.email,
        itemName: item?.name,
        selectedEdition: selectedEdition,
        price: item?.price,
        quantity: quantity,
        totalPrice: totalPrice,
        address: address,
        deliveryDate: finalDeliveryDate,
        deliveryType: deliveryOption,
        customerName: customerName,
        signature: signature,
        message: `Order: ${quantity}x ${item?.name} (${selectedEdition}) - Deliver to ${address} on ${finalDeliveryDate} (${deliveryOption}). Signed by: ${signature}`,
        type: 'order'
      })
    })
      .then(res => res.json())
      .then(() => {
        setOrdered(true);
        setTimeout(() => navigate('/dashboard'), 3000);
      })
      .catch(err => {
        console.error('Error placing order:', err);
        setError('Failed to place order.');
      });
  };

  if (loading) return <div className="booking-container"><div className="loading-state">Loading checkout...</div></div>;
  if (!item) return <div className="booking-container"><h2>Item not found</h2></div>;

  const currentShippingFee = deliveryOption === 'rush' ? RUSH_FEE : (isAfterCutoff ? LATE_FEE : 0);
  const currentTotalPrice = (item.price * quantity) + currentShippingFee;

  return (
    <div className="booking-container">
      <Link to={`/shopping/${id}`} className="back-link">← Back to Item</Link>
      <div className="booking-card">
        <div className="booking-header">
          <img src={item.image} alt={item.name} className="booking-icon" style={{ borderRadius: '10px' }} />
          <h1>Checkout: {item.name}</h1>
          <div style={{ marginTop: '10px' }}>
            <span style={{ fontSize: '1.5rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>
              Total: ${currentTotalPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {ordered ? (
          <div className="success-msg">
            <h2>🎉 Order Placed!</h2>
            <p>Your order for {quantity}x {item.name} ({selectedEdition}) has been received.</p>
            <p>Delivery scheduled for: <strong>{deliveryOption === 'rush' ? 'TOMORROW' : 'Sunday'}, {deliveryOption === 'rush' ? tomorrowDate : standardDate}</strong></p>
            <p>Payment of <strong>${currentTotalPrice.toFixed(2)}</strong> will be collected in cash upon delivery.</p>
          </div>
        ) : (
          <form className="booking-form" onSubmit={handleSubmit}>
            <div className="user-info-summary" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '12px' }}>
              <p style={{ margin: '0 0 5px 0' }}>Item: <strong>{item.name} ({selectedEdition})</strong></p>
              <p style={{ margin: '0 0 5px 0' }}>Subtotal: <strong>${(item.price * quantity).toFixed(2)}</strong></p>
              <p style={{ margin: 0 }}>Shipping: <strong style={{ color: currentShippingFee > 0 ? '#e74c3c' : '#4caf50' }}>{currentShippingFee > 0 ? `$${currentShippingFee.toFixed(2)}` : 'FREE'}</strong></p>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <div className="form-group">
              <label>Quantity</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="secondary-btn" style={{ padding: '5px 15px', minWidth: '40px' }}>-</button>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{quantity}</span>
                <button type="button" onClick={() => setQuantity(quantity + 1)} className="secondary-btn" style={{ padding: '5px 15px', minWidth: '40px' }}>+</button>
              </div>
            </div>

            <div className="form-group">
              <label>Your Full Name</label>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Name for delivery" required />
            </div>

            <div className="form-group">
              <label>Delivery Address (Nova Scotia)</label>
              <AddressAutocomplete value={address} onChange={setAddress} placeholder="e.g. 123 Maple St, Apt 4" required />
            </div>

            <div className="form-group">
              <label>Delivery Speed</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  padding: '15px', 
                  borderRadius: '12px', 
                  border: deliveryOption === 'standard' ? '2px solid var(--primary-color)' : '1px solid #ddd',
                  backgroundColor: deliveryOption === 'standard' ? '#f0f7ff' : 'white',
                  cursor: 'pointer'
                }}>
                  <input type="radio" checked={deliveryOption === 'standard'} onChange={() => setDeliveryOption('standard')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>Standard Delivery ({isAfterCutoff ? `$${LATE_FEE.toFixed(2)}` : 'FREE'})</div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Next Sunday ({standardDate}). {isAfterCutoff ? 'Note: Wednesday cutoff passed.' : 'Free shipping ends Wednesday night!'}</div>
                  </div>
                </label>
                
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  padding: '15px', 
                  borderRadius: '12px', 
                  border: deliveryOption === 'rush' ? '2px solid #e74c3c' : '1px solid #ddd',
                  backgroundColor: deliveryOption === 'rush' ? '#fff5f5' : 'white',
                  cursor: 'pointer'
                }}>
                  <input type="radio" checked={deliveryOption === 'rush'} onChange={() => setDeliveryOption('rush')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', color: '#e74c3c' }}>🚀 RUSH Delivery (+$5.00)</div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Get it TOMORROW ({tomorrowDate})!</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#fafafa', border: '2px dashed #ddd', borderRadius: '12px' }}>
              <label style={{ fontSize: '1rem', color: '#333' }}>🖋️ Digital Signature (Type Full Name)</label>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '-5px', marginBottom: '15px' }}>
                By signing below, you agree to pay **CASH** upon delivery and confirm the address is correct.
              </p>
              <input 
                type="text" 
                value={signature} 
                onChange={(e) => setSignature(e.target.value)} 
                placeholder="Type your name to sign"
                style={{ fontFamily: '"Caveat", cursive', fontSize: '1.5rem', padding: '10px 15px', border: 'none', borderBottom: '2px solid #333', background: 'transparent', width: '100%' }}
                required 
              />
            </div>

            <button type="submit" className="book-submit-btn" style={{ marginTop: '20px' }} disabled={!signature.trim()}>
              Place Order - Pay ${currentTotalPrice.toFixed(2)} on Delivery
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Checkout;
